import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  Select,
  Spin,
  Tooltip,
  Upload,
} from "antd";
import React, { useEffect } from "react";
import { useClient } from "../../../Context/ClientContext";
import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { notificationContext } from "../../../Common/Routing/PageRoute";
import AssetCategoriesService, {
  AssetCategories_Type,
  AssetCategoriesTypesList,
  IFACategoryList,
} from "../../../Services/AssetCategoriesService";
import { useHotkey } from "../../../Context/HotKeyContext";
import EntryLevelKeys from "../../../Common/EntryLevelKeys";

interface Modelprops {
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  isModelOpen: boolean;
  id?: string;
  setAddAssCategory?: React.Dispatch<React.SetStateAction<IFACategoryList>>;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
}
const AddAssetCategoryModal = ({
  isModelOpen,
  onCancel,
  id,
  setRefresh,
  setAddAssCategory,
}: Modelprops) => {
  const { showKeys } = useHotkey();
  const [form] = Form.useForm();
  const { currentClient } = useClient();
  const [deleteUploadId, setDeleteUploadId] = React.useState<string[]>([]);
  const [assetCategoryListData, setAssetCategoryListData] = React.useState<
    IFACategoryList[]
  >([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [btnLoading, setBtnLoading] = React.useState<boolean>(false);
  const { openNotification } = React.useContext(notificationContext);
  const [previewImage, setPreviewImage] = React.useState<{
    isBaseConvert: boolean;
    path: any;
  }>({ isBaseConvert: false, path: "" });

  const addCategoryData = () => {
    form.validateFields().then(async (val) => {
      setBtnLoading(true);
      let res = await AssetCategoriesService?.postCategoryData(
        currentClient?.id,
        val,
        id
      );
      if (res?.status) {
        form.resetFields();
        setBtnLoading(false);
        setRefresh && setRefresh((x: boolean) => !x);
        openNotification(
          "success",
          `Category ${id ? "updated successfully." : "added successfully."}`
        );
        let parentCategoryName = assetCategoryListData?.find(
          (cat) => cat.id === res.result?.parentCategoryId
        )?.categoryName;
        setAddAssCategory &&
          setAddAssCategory({
            ...res.result,
            parentCategoryName: parentCategoryName,
          });
        onCancel(false);
      } else {
        setBtnLoading(false);
        openNotification("error", res?.message);
      }
    });
  };

  const getAssetCategoriesList = async () => {
    await AssetCategoriesService?.getCategoriesList(
      currentClient?.id,
      0,
      0,
      ""
    ).then((res) => {
      if (res?.totalRecords! > 0) {
        setAssetCategoryListData(
          res?.items.filter((category) => category.id !== id) || []
        );
      }
    });
  };

  useEffect(() => {
    if (id) {
      const getDatabyId = async () => {
        setLoading(true);
        let res = await AssetCategoriesService?.getCategoryDatabyId(
          currentClient?.id,
          id
        );
        if (res) {
          form?.setFieldsValue(res?.result);
          setLoading(false);
        } else {
          setLoading(false);
        }
      };
      getDatabyId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getAssetCategoriesList();
  }, [currentClient?.id]);

  EntryLevelKeys({
    save: () => addCategoryData(),
    cancel: () => onCancel(false),
  });
  return (
    <div>
      <Drawer
        open={isModelOpen}
        onClose={() => onCancel(false)}
        width={"30vw"}
        title={`${id ? "Update" : "Add"} Category`}
        extra={
          <div style={{ marginTop: "0px" }}>
            <Tooltip
              title={<p>ALT+C</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                onClick={() => onCancel(false)}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </Button>
            </Tooltip>
            <Tooltip
              title={<p>ALT+S</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                loading={btnLoading}
                type="primary"
                onClick={addCategoryData}
              >
                {id ? "Update" : "Save"}
              </Button>
            </Tooltip>
          </div>
        }
        maskClosable={false}
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 20 }} />}
          spinning={loading}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: "20px" }}
            initialValues={{
              assetCategoryType: AssetCategories_Type.Unspecified,
            }}
          >
            <Form.Item
              name="categoryName"
              label="Category Name"
              rules={[
                {
                  required: true,
                  message: "Please enter asset category name",
                },
                {
                  min: 3,
                  max: 30,
                  message:
                    "Asset category name's length must be minimum 3 and maximum 30",
                },
              ]}
            >
              <Input maxLength={30} />
            </Form.Item>
            <Form.Item name="parentCategoryId" label="Parent Category">
              <Select
                options={assetCategoryListData?.map((category) => ({
                  key: category?.id,
                  title: "",
                  value: category?.id,
                  label: category?.categoryName,
                }))}
              />
            </Form.Item>
            <Form.Item name="assetCategoryType" label="Asset Category Type">
              <Select
                showSearch
                placeholder="Search to select"
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                options={AssetCategoriesTypesList}
              />
            </Form.Item>
            <Form.Item
              name="maintainLocation"
              label="Maintain Location"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="maintainModel"
              label="Maintain Model No."
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="maintainIdentityMark"
              label="Maintain Identity Mark"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="maintainWarranty"
              label="Maintain Warranty"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="maintainVariant"
              label="Maintain Variant"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="maintainOption"
              label="Maintain Option"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            {/* Added Maintain Employee Assign checkbox */}
            <Form.Item
              name="maintainEmployeeAssign"
              label="Maintain Employee Assign"
              layout="horizontal"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              name="attachments"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                return e.fileList;
              }}
              // className="m-0"
              style={{ paddingTop: "10px", margin: "0px" }}
            >
              <Upload
                multiple={false}
                maxCount={1}
                accept={".png, .jpg, .jpeg, .pdf"}
                beforeUpload={(file) => {
                  if (file.size > 1024 * 300) {
                    openNotification(
                      "error",
                      `${file.name} size is more than 300 KB`
                    );
                    return Upload.LIST_IGNORE;
                  }
                  let imgArray: string[] = [
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "application/pdf",
                  ];
                  if (!imgArray.some((type) => type === file.type)) {
                    openNotification(
                      "error",
                      `${file.name} has invalid file format`
                    );
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onRemove={(e: any) => {
                  if (id && e?.id) {
                    setDeleteUploadId([...deleteUploadId, e?.id]);
                  }
                }}
                onPreview={(e: any) => {
                  if (id && e?.path) {
                    setPreviewImage({
                      isBaseConvert: false,
                      path: e,
                    });
                  } else if (e.type?.split("/")[0] === "image") {
                    setPreviewImage({
                      isBaseConvert: true,
                      path: e,
                    });
                  }
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={{ backgroundColor: "ButtonFace" }}
                >
                  Uploads
                </Button>
              </Upload>
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};

export default AddAssetCategoryModal;
